/* eslint-disable unicorn/no-array-for-each */

import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNoticeBannersStackContext } from '@sweetgreen/garnish';

import { useCustomer } from '@order/Customer';
import { useLocalizationContext } from '@order/Localization';

// ────────────────────────────────────────────────────────────────────────────────

export const useLoginForm = () => {
  const { t } = useLocalizationContext();
  const { signInResponseData } = useCustomer();
  const { push: addBanner } = useNoticeBannersStackContext();

  // ────────────────────────────────────────────────────────────────────

  const requiredEmailMessage = t('form.email-required');
  const requiredPasswordMessage = t('form.password-required');
  const invalidEmail = t('form.validation-email');

  const validationSchema = useMemo(
    () =>
      yup
        .object({
          email: yup
            .string()
            .required(requiredEmailMessage)
            .email(invalidEmail),
          password: yup.string().required(requiredPasswordMessage),
        })
        .required(),
    [invalidEmail, requiredEmailMessage, requiredPasswordMessage],
  );

  // ────────────────────────────────────────────────────────────────────

  const form = useForm<LoginFormData>({
    defaultValues: { email: '', password: '' },
    resolver: yupResolver(validationSchema),
    mode: 'onBlur',
  });

  const showVerificationErrors = useCallback(
    (fieldsErrors: LoginFieldsErrors) => {
      fieldsErrors.forEach((fieldError) => {
        const { field, message } = fieldError;

        const fieldName = field as keyof LoginFormData;

        form.setError(fieldName, { type: 'validate', message });
      });
    },
    [form],
  );

  // ─── EFFECTS ────────────────────────────────────────────────────────

  // ─── OPS-customer-decoupling-login-enabled > clean-up ───────────────

  // handle login mutation errors
  useEffect(() => {
    const validationErrorCase =
      signInResponseData?.__typename === 'ValidationError';

    if (!validationErrorCase) return;

    showVerificationErrors(signInResponseData.fieldErrors);
  }, [addBanner, form, showVerificationErrors, signInResponseData]);

  // ────────────────────────────────────────────────────────────────────

  return { form, showVerificationErrors };
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

export type LoginFormData = {
  readonly email: string;
  readonly password: string;
  readonly grecaptcha?: string;
};

type LoginFieldsErrors = ReadonlyArray<{
  readonly field: string;
  readonly message: string;
}>;
